import { Checkbox, Typography } from '@smartgift/frontend-toolkit';
import dayjs from 'dayjs';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { dateMaskTrack } from '@hero/components/atoms';
import { dateLabelFormatDay, dateLabelFormatDayWithYear } from '@hero/constants';
import { useIsAdminOrganization } from '@hero/hooks';
import { Table, TableName } from '@hero/molecules';
import { ROUTE_PATHS } from '@hero/routes';
import {
  useGetContacts,
  useGetTagList,
  useUpdateContact
} from '@hero/services/contact-service/hooks';
import { useGetUser } from '@hero/services/organization-service/hooks';
import { checkFormatContainsYear } from '@hero/utils/dateUtils';

import { NewContactModal } from '../../NewContactModal';

import { CustomSelectionHead } from './CustomSelectionHead';
import * as S from './style';

const AdvanceContactTable = ({
  selectedRows,
  disabledRows,
  onChangeSelectedRows,
  defaultPageSize = 50,
  enabled = { filter: true, header: true },
  defaultGlobalFilters = {},
  defaultParams = {},
  components = {},
  defaultFields = ['select', 'name', 'email', 'tags', 'createdBy'],
  selectedTag
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: defaultPageSize
  });
  const [globalFilters, setGlobalFilters] = useState(defaultGlobalFilters);
  const [rowSelection, setRowSelection] = useState(selectedRows || {});
  const [showingSelectedItems, setShowingSelectedItems] = useState(false);
  const [openNewContactsModal, setOpenNewContactsModal] = useState();

  const rowSelectionArray = useMemo(() => {
    return Object.keys(rowSelection || {});
  }, [rowSelection]);

  const { mutateAsync: updateContact } = useUpdateContact();
  const { data: filterOptions } = useGetTagList();
  const { data: user } = useGetUser();
  const isOrganizationAdmin = useIsAdminOrganization();

  const { data: contacts, isLoading } = useGetContacts(
    Object.assign(
      {
        filters: {
          searchTerm: globalFilters.searchTerm,
          tags: globalFilters.filters?.map(filter => filter.value),
          ids: showingSelectedItems ? rowSelectionArray : undefined
        },
        pagination: {
          page: pageIndex + 1,
          limit: pageSize
        }
      },
      defaultParams
    )
  );

  function firstNameCell({
    row: {
      original: { firstName, lastName, id }
    }
  }) {
    return (
      <TableName
        firstName={firstName}
        lastName={lastName}
        onClick={() => {
          navigate(ROUTE_PATHS.CONTACT_DETAIL(id));
        }}
      />
    );
  }

  const columns = React.useMemo(() => {
    const allColumns = [
      {
        id: 'select',
        maxSize: 30,
        header: ({ table }) => {
          return (
            <Checkbox
              {...{
                key: table.getState().pagination?.pageIndex,
                checked: table.getIsAllPageRowsSelected(),
                indeterminate: table.getIsSomePageRowsSelected(),
                onChange: table.getToggleAllPageRowsSelectedHandler()
              }}
            />
          );
        },
        cell: ({ row, table }) => {
          const { disabledRows } = table.getState();
          return (
            <Checkbox
              {...{
                checked: disabledRows?.[row.original?.id] || row.getIsSelected(),
                disabled: disabledRows?.[row.original?.id],
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler()
              }}
            />
          );
        }
      },
      {
        id: 'name',
        header: t('name'),
        size: 220,
        accessorKey: 'firstName',
        cell: firstNameCell
      },
      {
        id: 'email',
        header: t('email'),
        editable: true,
        accessorKey: 'email',
        componentProps: {
          maxLength: 50
        },
        cell: ({
          cell: {
            row: {
              original: { email }
            }
          }
        }) => <S.Email>{email}</S.Email>
      },
      {
        id: 'birthday',
        header: t('birthday'),
        editable: true,
        size: 200,
        type: 'mask',
        accessorFn: e => e.birthday?.full,
        componentProps: {
          mask: 'mm/dd/yyyy',
          track: dateMaskTrack,
          isValid: v => v?.length === 5 || v?.length === 10,
          errorText: t('invalidBirthday'),
          formatPreview: v => {
            const withYear = checkFormatContainsYear(v);

            return v
              ? dayjs(v, withYear ? 'MM/DD/YYYY' : 'MM/DD').format(
                  withYear ? dateLabelFormatDayWithYear : dateLabelFormatDay
                )
              : '';
          },
          replacement: { d: /\d/, m: /\d/, y: /\d/ },
          placeholder: t('dayAndMonthOrYearPlaceholder')
        }
      },
      {
        id: 'anniversary',
        header: t('workAnniversary'),
        editable: true,
        size: 200,
        type: 'mask',
        accessorFn: e => e.anniversary?.full,
        componentProps: {
          mask: 'mm/dd/yyyy',
          track: dateMaskTrack,
          errorText: t('invalidAnniversary'),
          isValid: v => v?.length === 5 || v?.length === 10,
          formatPreview: v => {
            const withYear = checkFormatContainsYear(v);

            return v
              ? dayjs(v, withYear ? 'MM/DD/YYYY' : 'MM/DD').format(
                  withYear ? dateLabelFormatDayWithYear : dateLabelFormatDay
                )
              : '';
          },
          placeholder: t('dayAndMonthOrYearPlaceholder'),
          replacement: { d: /\d/, m: /\d/, y: /\d/ }
        }
      }
    ];

    if (isOrganizationAdmin) {
      allColumns.push({
        id: 'createdBy',
        header: t('createdBy'),
        editable: false,
        accessorKey: 'user',
        cell: ({ getValue }) => {
          const value = getValue();
          return value?.firstName ? (
            <Typography variant="body2">{`${value?.firstName} ${value?.lastName}`}</Typography>
          ) : (
            ''
          );
        }
      });
    }

    allColumns.push({
      id: 'tags',
      header: 'Tag',
      size: 430,
      accessorFn: originalRow => {
        return originalRow?.tags?.map(tag => ({
          label: tag.name,
          value: tag.id
        }));
      },
      editable: true,
      type: 'tags'
    });

    return allColumns.filter(column => defaultFields.includes(column.id));
  }, [defaultFields, user?.organization]);

  const updateData = (originalRow, columnId, value) => {
    const currentValue = originalRow[columnId];

    if (['anniversary', 'birthday'].includes(columnId) && currentValue.full === value) {
      return;
    }

    const isDifferent = isArray(value)
      ? !isEqual(
          currentValue?.map?.(col => col.id),
          value?.map?.(val => val.value)
        )
      : !isEqual(currentValue, value);

    if (!isDifferent) return;

    updateContact({
      id: originalRow.id,
      body: { [columnId]: value }
    });
  };

  React.useEffect(() => {
    if (selectedRows && !isEqual(selectedRows, rowSelection)) {
      setRowSelection(selectedRows);
    }
  }, [selectedRows]);

  React.useEffect(() => {
    if (onChangeSelectedRows) onChangeSelectedRows(rowSelection);
  }, [rowSelection]);

  return (
    <>
      <Table
        columns={columns}
        data={contacts?.docs || []}
        globalFilters={globalFilters}
        state={{
          setShowingSelectedItems,
          showingSelectedItems,
          disabledRows
        }}
        pagination={{
          pageIndex,
          pageSize,
          total: contacts?.pagination?.total
        }}
        paginationProps={{
          perPageSelection: true,
          rowsPerPageOptions: [10, 20, 30, 50]
        }}
        updateData={updateData}
        filterOptions={filterOptions?.docs?.map(doc => ({ label: doc.name, value: doc.id })) || []}
        manualPagination
        loading={isLoading}
        setGlobalFilters={setGlobalFilters}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        setPagination={setPagination}
        pageCount={contacts?.pagination?.pages}
        enabled={enabled}
        translations={{
          selectedRowCountText: t('selectedRowCount', {
            selectedRowCount: rowSelectionArray.length,
            totalContactCount: contacts?.pagination?.total
          }),
          filteredWithText: t('filteredWithText', {
            filteredContactCount: contacts?.pagination?.total || '0'
          })
        }}
        filterProps={{
          placeholder: t('filterByType', { type: t('tags') }),
          searchPlaceholder: t('searchTagOnFilterPlaceholder')
        }}
        components={{
          SelectionHead: CustomSelectionHead,
          ...components
        }}
        emptyTableContent={{
          feature: 'contacts',
          icon: 'contacts',
          createNew: () => setOpenNewContactsModal(true)
        }}
      />

      {openNewContactsModal && (
        <NewContactModal
          openModal={openNewContactsModal}
          onRequestClose={() => setOpenNewContactsModal(false)}
          onUpdatingDone={() => {
            setOpenNewContactsModal(false);
          }}
          selectedTag={selectedTag}
        />
      )}
    </>
  );
};

export { AdvanceContactTable };
